import React, { useEffect, useMemo, useState } from 'react'
import { Volume2 } from 'lucide-react'
import PauseIcon from 'components/Common/SvgIcons/PauseIcon'
import { AppWaveformVisualizer } from 'components/Common/AppWaveformVisualizer/AppWaveformVisualizer.component'

interface AppVoicePlaybackProps {
  isActive: boolean
  isPlaying: boolean
  duration?: number
  onToggle: () => void
  className?: string
}

const MIN_DURATION = 1

export const AppVoicePlayback: React.FC<AppVoicePlaybackProps> = ({
  isActive,
  isPlaying,
  duration,
  onToggle,
  className = '',
}) => {
  const [elapsed, setElapsed] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)
  // Store the initial duration when playback starts to preserve it when paused
  const [storedDuration, setStoredDuration] = useState<number | undefined>(undefined)
  // Track previous isActive state to detect when playback ends naturally
  const prevIsActiveRef = React.useRef<boolean>(false)

  // Calculate safe duration: use stored duration if available, otherwise use prop, fallback to MIN_DURATION
  const safeDuration = useMemo(() => {
    const durationToUse = storedDuration ?? duration
    return Math.max(MIN_DURATION, Math.round(durationToUse || MIN_DURATION))
  }, [storedDuration, duration])

  useEffect(() => {
    if (isActive && !hasStarted) {
      // Starting a new playback: reset everything
      setElapsed(0)
      if (duration !== undefined) {
        setStoredDuration(duration)
      }
    }
    // When not active, we fully reset in a separate effect below
  }, [isActive, hasStarted, duration])

  useEffect(() => {
    // Continue tracking elapsed as long as playback has started and hasn't finished
    if (!hasStarted || elapsed >= safeDuration) {
      return
    }

    // Only increment elapsed when actually playing
    if (!isActive || !isPlaying) {
      return
    }

    // Update every 100ms for smoother animation (0.1 second increments)
    const interval = window.setInterval(() => {
      setElapsed((prev) => {
        if (prev + 0.1 >= safeDuration) {
          return safeDuration
        }
        return prev + 0.1
      })
    }, 100)

    return () => {
      window.clearInterval(interval)
    }
  }, [hasStarted, isActive, isPlaying, safeDuration, elapsed])

  // Manage hasStarted state: set to true when playback starts, keep it true when paused
  useEffect(() => {
    if (isActive && !hasStarted) {
      setHasStarted(true)
      // Store the initial duration when playback first starts
      if (duration !== undefined) {
        setStoredDuration(duration)
      }
    }
    // Don't reset hasStarted when isActive becomes false - keep it true when paused
  }, [isActive, hasStarted, duration])

  // Detect when playback ends naturally (isActive transitions from true to false)
  useEffect(() => {
    // If playback was active and now it's not, and we've started
    if (prevIsActiveRef.current && !isActive && hasStarted && !isPlaying) {
      // Check if we're close to completion (within 3 seconds or 80% of duration)
      // This helps distinguish between natural completion and user pause
      const isNearCompletion = elapsed >= safeDuration * 0.8 || elapsed >= safeDuration - 3

      if (isNearCompletion) {
        // Speech likely completed naturally - reset immediately
        setHasStarted(false)
        setElapsed(0)
        setStoredDuration(undefined)
      }
      // If not near completion, it's likely a pause - keep the UI showing
    }
    prevIsActiveRef.current = isActive
    return undefined
  }, [isActive, hasStarted, isPlaying, safeDuration, elapsed])

  // Also reset when elapsed reaches duration (backup mechanism)
  useEffect(() => {
    if (elapsed >= safeDuration && hasStarted) {
      setHasStarted(false)
      setElapsed(0)
      setStoredDuration(undefined) // Clear stored duration when playback completes
    }
  }, [elapsed, safeDuration, hasStarted])

  const progressRatio = useMemo(() => {
    if (safeDuration === 0) return 0
    return Math.min(elapsed / safeDuration, 1)
  }, [elapsed, safeDuration])

  // Calculate segment count: 5 segments per second for smoother animation
  const segmentCount = useMemo(() => {
    return Math.max(Math.round(safeDuration * 5), 20) // Minimum 20 segments for very short audio
  }, [safeDuration])

  // Calculate dynamic segment width based on count - doubled to allow wider waveform
  const segmentWidthClass = useMemo(() => {
    if (segmentCount >= 200) return 'w-[1px]'
    if (segmentCount >= 100) return 'w-[2px]'
    if (segmentCount >= 50) return 'w-[3px]'
    return 'w-[4px]' // Default for fewer segments
  }, [segmentCount])

  // Calculate dynamic gap between segments
  const segmentGap = useMemo(() => {
    if (segmentCount >= 200) return 'gap-0'
    if (segmentCount >= 100) return 'gap-[0.5px]'
    return 'gap-[1px]' // Default gap
  }, [segmentCount])

  return (
    <div className={`flex flex-col items-start gap-2 ${className}`}>
      {!hasStarted && (
        <button
          type="button"
          onClick={onToggle}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
            isActive
              ? 'bg-primary-appBrand text-white shadow-sm'
              : 'bg-primary-appBrand/15 text-primary-appBrand'
          }`}
          aria-label={isActive ? 'Stop voice playback' : 'Play voice playback'}
        >
          <Volume2 className="w-4 h-4" />
        </button>
      )}

      {hasStarted && (
        <div className="w-full">
          <div className="flex items-center gap-3 bg-app-white-99 border border-app-primary-30 rounded-[999px] px-4 py-2 shadow-sm min-w-[260px]">
            <button
              type="button"
              onClick={onToggle}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                isPlaying
                  ? 'bg-primary-appBrand text-white shadow-sm'
                  : 'bg-primary-appBrand/15 text-primary-appBrand'
              }`}
              aria-label={isPlaying ? 'Pause voice playback' : 'Resume voice playback'}
            >
              {isPlaying ? <PauseIcon className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <AppWaveformVisualizer
              mode="progress"
              progress={progressRatio}
              segmentCount={segmentCount}
              randomizeHeights={true}
              minHeight={4}
              maxHeight={16}
              activeColorClass="bg-primary-appBrand"
              inactiveColorClass="bg-primary-appBrand/30"
              segmentWidthClass={segmentWidthClass}
              className="flex-1 h-8"
              segmentGap={segmentGap}
            />
          </div>
        </div>
      )}
    </div>
  )
}
